import React, { useEffect, useState } from 'react';
import EditorParent from '@/base-components/EditorParent';
import { Box, Center, Input, SimpleGrid, Table as NativeTable, Textarea } from '@chakra-ui/react';
import { useMutation, useQuery } from 'react-query';
import { getTemplates, updateTemplate, previewTemplate, Template } from '@/api/template-controller';
import { searchForOrders } from '@/api/order-controller';
import { customerName } from '@/generic/customerName';
import Dropdown from '@/base-components/Dropdown';
import { DropdownItem } from '@/api/base/dropdown-item';
import { Button } from '@/components/ui/button';
import useToast from '@/base-components/hooks/useToast';
import { SPACING } from '@/generic/config';
import Text from '@/base-components/Text';
import { Context } from '@/App';
import { QueryKeys } from '@/api/base/react-query';
import { QUERY_CLIENT } from '@/index';

const in3Months = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d.toISOString().split('T')[0];
};

// Available placeholders + default sample values. Editable in the UI so authors
// can try different values and see conditional {{#...}} sections show/hide.
const placeholders: { key: string; example: string }[] = [
    { key: "worksiteName", example: "Massan 4" },
    { key: "worksiteAddress", example: "Metergatan 10" },
    { key: "worksitePostalCode", example: "50743" },
    { key: "worksiteCity", example: "Borås" },
    { key: "productsSummary", example: "<ul><li>5 x Kärl 100L</li><li>10 x Påse</li></ul>" },
    { key: "nextServiceDate", example: in3Months() },
];
const defaultValues = (): Record<string, string> =>
    Object.fromEntries(placeholders.map((p) => [p.key, p.example]));

const TemplateEditor: React.FC = () => {
    const { translations } = React.useContext(Context);
    const toast = useToast();
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const [content, setContent] = useState<string>("");
    const [subject, setSubject] = useState<string>("");
    const [values, setValues] = useState<Record<string, string>>(defaultValues);
    const [previewOrderId, setPreviewOrderId] = useState<number | null>(null);
    const [previewHtml, setPreviewHtml] = useState<string>("");

    const { data: templates } = useQuery(QueryKeys.templates, getTemplates);
    // Orders for the "preview with a real order" picker.
    const { data: ordersPage } = useQuery('templatePreviewOrders',
        () => searchForOrders("", null, null, null, null, 0, 100));

    // Preview is rendered by the backend (same renderer as real emails), debounced.
    // With an order picked it renders that order's real data; otherwise the sample values.
    useEffect(() => {
        if (!selectedTemplate) {
            setPreviewHtml("");
            return;
        }
        const handle = setTimeout(() => {
            previewTemplate(content, values, previewOrderId)
                .then(setPreviewHtml)
                .catch(() => setPreviewHtml("<p style='color:#b00'>Kunde inte rendera förhandsvisning.</p>"));
        }, 300);
        return () => clearTimeout(handle);
    }, [content, values, previewOrderId, selectedTemplate]);

    const orderItems: DropdownItem[] = (ordersPage?.items ?? []).map((o) => ({
        id: String(o.id),
        name: `${o.externalId || '#' + o.id} – ${customerName(o.worksiteHit?.customerHit) || '—'}`,
    }));

    const { mutate: doUpdate } = useMutation(
        () => updateTemplate(selectedTemplate!.id, { ...selectedTemplate!, content, subject }),
        {
            onSuccess: (updated) => {
                setSelectedTemplate(updated);
                QUERY_CLIENT.invalidateQueries(QueryKeys.templates);
                toast(translations.save.saved.title, translations.save.saved.description, "success");
            }
        }
    );

    const templateItems: DropdownItem[] = templates?.map(t => ({
        id: t.templateType,
        name: t.templateType.replace(/_/g, ' '),
    })) ?? [];

    const onSelectTemplate = (item: DropdownItem | null) => {
        if (item) {
            const template = templates?.find(t => t.templateType === item.id);
            if (template) {
                setSelectedTemplate(template);
                setContent(template.content ?? "");
                setSubject(template.subject ?? "");
            }
        } else {
            setSelectedTemplate(null);
            setContent("");
            setSubject("");
        }
    };

    const isDirty = selectedTemplate && (content !== (selectedTemplate.content ?? "") || subject !== (selectedTemplate.subject ?? ""));

    return (
        <EditorParent title={translations.templateEditor.title} description={translations.templateEditor.description}>
            <Box mb={SPACING}>
                <Dropdown
                    title={translations.templateEditor.selectTemplate}
                    placeholder={translations.templateEditor.selectTemplatePlaceholder}
                    items={templateItems}
                    value={selectedTemplate ? { id: selectedTemplate.templateType, name: selectedTemplate.templateType.replace(/_/g, ' ') } : null}
                    onChange={onSelectTemplate}
                />
            </Box>
            {selectedTemplate && (
                <>
                    <Box mb={SPACING}>
                        <Text weight="semibold" variant="normal" mb={SPACING / 4}>
                            {translations.templateEditor.subject}
                        </Text>
                        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                    </Box>
                    <Box mb={SPACING}>
                        <Dropdown
                            title={translations.templateEditor.previewOrder}
                            placeholder={translations.templateEditor.previewOrderPlaceholder}
                            items={orderItems}
                            value={previewOrderId != null
                                ? (orderItems.find((i) => i.id === String(previewOrderId)) ?? null)
                                : null}
                            onChange={(item) => setPreviewOrderId(item ? Number(item.id) : null)}
                        />
                    </Box>
                    <SimpleGrid columns={2} gap={SPACING}>
                        <Box>
                            <Text weight="semibold" variant="normal" mb={SPACING / 4}>
                                {translations.templateEditor.content}
                            </Text>
                            <Textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                minH="750px"
                            />
                        </Box>
                        <Box>
                            <Text weight="semibold" variant="normal" mb={SPACING / 4}>
                                {translations.templateEditor.preview}
                            </Text>
                            <iframe
                                title="html-preview"
                                srcDoc={previewHtml}
                                style={{ width: '100%', height: '750px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                            />
                        </Box>
                    </SimpleGrid>
                    <Box mt={SPACING * 2}>
                        <Text weight="semibold" variant="normal" mb={SPACING / 4}>
                            {translations.templateEditor.placeholders}
                        </Text>
                        <Text variant="small" mb={SPACING / 2}>
                            {previewOrderId != null
                                ? translations.templateEditor.placeholdersIgnored
                                : translations.templateEditor.placeholdersHint}
                        </Text>
                        <NativeTable.Root size="sm">
                            <NativeTable.Header>
                                <NativeTable.Row>
                                    <NativeTable.ColumnHeader>{translations.templateEditor.placeholder}</NativeTable.ColumnHeader>
                                    <NativeTable.ColumnHeader>{translations.templateEditor.exampleValue}</NativeTable.ColumnHeader>
                                </NativeTable.Row>
                            </NativeTable.Header>
                            <NativeTable.Body>
                                {placeholders.map(p => (
                                    <NativeTable.Row key={p.key}>
                                        <NativeTable.Cell fontFamily="mono">{`{{${p.key}}}`}</NativeTable.Cell>
                                        <NativeTable.Cell>
                                            <Input
                                                size="sm"
                                                value={values[p.key] ?? ""}
                                                onChange={(e) => setValues((prev) => ({ ...prev, [p.key]: e.target.value }))}
                                            />
                                        </NativeTable.Cell>
                                    </NativeTable.Row>
                                ))}
                            </NativeTable.Body>
                        </NativeTable.Root>
                    </Box>
                    {isDirty && (
                        <>
                            <Box width="100%" py={SPACING * 4} />
                            <Box position="fixed" bottom={0} width="100%" zIndex={99} left={0} backgroundColor="gray" py={SPACING / 2}>
                                <Center>
                                    <Button px={SPACING * 2} onClick={() => doUpdate()}>
                                        {translations.save.save}
                                    </Button>
                                </Center>
                            </Box>
                        </>
                    )}
                </>
            )}
        </EditorParent>
    );
};

export default TemplateEditor;
